import React, { useMemo, useState } from 'react';
import { HodAvatar, hodFullDate, hodTimeAgo } from './HodThread';

export { HodAvatar as LabAvatar, hodFullDate as labFullDate, hodTimeAgo as labTimeAgo };

export const LAB_STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200', dot: 'bg-amber-400' },
  'in process': { label: 'In process', badge: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200', dot: 'bg-blue-500' },
  'on hold': { label: 'On hold', badge: 'bg-violet-100 text-violet-800 ring-1 ring-violet-200', dot: 'bg-violet-500' },
  solved: { label: 'Solved', badge: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200', dot: 'bg-emerald-500' },
};

export const LAB_FLAGS = [
  {
    key: 'normal', label: 'Normal', hint: 'Waits its turn in the queue',
    chip: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200', stripe: 'border-l-gray-300',
    select: 'border-gray-300 bg-gray-50 text-gray-800 ring-gray-200',
  },
  {
    key: 'important', label: 'Important', hint: 'Needs attention soon',
    chip: 'bg-sky-100 text-sky-800 ring-1 ring-sky-200', stripe: 'border-l-sky-400',
    select: 'border-sky-400 bg-sky-50 text-sky-800 ring-sky-200',
  },
  {
    key: 'urgent', label: 'Urgent', hint: 'Needed as soon as possible',
    chip: 'bg-orange-100 text-orange-800 ring-1 ring-orange-200', stripe: 'border-l-orange-500',
    select: 'border-orange-400 bg-orange-50 text-orange-800 ring-orange-200',
  },
  {
    key: 'very_important', label: 'Very important', hint: 'Lab assistant can take it out of turn',
    chip: 'bg-red-600 text-white ring-1 ring-red-700', stripe: 'border-l-red-600',
    select: 'border-red-500 bg-red-50 text-red-700 ring-red-200',
  },
];
export const LAB_FLAG_META = Object.fromEntries(LAB_FLAGS.map((f) => [f.key, f]));

export function LabStatusBadge({ status, className = '' }) {
  const meta = LAB_STATUS_META[status] || LAB_STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.badge} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

export function LabFlagChip({ flag, className = '' }) {
  const meta = LAB_FLAG_META[flag] || LAB_FLAG_META.normal;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${meta.chip} ${className}`}>
      {flag === 'very_important' && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white" />}
      {meta.label}
    </span>
  );
}

// Create / edit form shared by the admin page (new + edit).
export function LabJobForm({ initial, submitLabel, busy, onSubmit, onCancel }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [location, setLocation] = useState(initial?.location || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [flag, setFlag] = useState(initial?.flag || 'normal');

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    const ok = await onSubmit({ title: title.trim(), location: location.trim(), description: description.trim(), flag });
    if (ok && !initial) {
      setTitle('');
      setLocation('');
      setDescription('');
      setFlag('normal');
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Job title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="e.g. Install VS Code on Lab 2 computers"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Lab / place <span className="font-normal normal-case text-gray-400">(optional)</span></label>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={255}
            placeholder="e.g. Lab 2"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Details <span className="font-normal normal-case text-gray-400">(optional)</span></label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="What exactly needs to be done?"
          className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Flag</label>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {LAB_FLAGS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFlag(f.key)}
              className={`rounded-lg border-2 px-3 py-2 text-left transition ${flag === f.key ? `${f.select} ring-2` : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
            >
              <span className="block text-sm font-bold">{f.label}</span>
              <span className="block text-[11px] leading-tight opacity-80">{f.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={busy || !title.trim()}
          className="rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function RoleChip({ role }) {
  return role === 'lab' ? (
    <span className="rounded bg-teal-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Lab</span>
  ) : (
    <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-600">Requester</span>
  );
}

// Facebook-style conversation for one job: comment bubbles and inline status
// notes in one time-ordered feed, then a composer.
export function LabJobThread({ job, currentStaffId, isSuperAdmin = false, onComment, onDeleteComment, composerPlaceholder }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const feed = useMemo(() => {
    const items = [];
    job.comments.forEach((c) => items.push({ kind: 'comment', at: new Date(c.createdAt).getTime(), key: `c${c.commentId}`, data: c }));
    // First log row is just "job created" -- the card already says that.
    job.statusLog.slice(1).forEach((l) => items.push({ kind: 'status', at: new Date(l.changedAt).getTime(), key: `s${l.logId}`, data: l }));
    return items.sort((a, b) => a.at - b.at);
  }, [job.comments, job.statusLog]);

  const submit = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      const ok = await onComment(job.jobId, value);
      if (ok !== false) setText('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {feed.length === 0 ? (
        <p className="py-3 text-center text-xs text-gray-400">No updates yet.</p>
      ) : (
        <div className="space-y-3 py-1">
          {feed.map((item) => {
            if (item.kind === 'status') {
              const l = item.data;
              return (
                <div key={item.key} className="flex items-center justify-center gap-2 text-[11px] text-gray-400">
                  <span className="h-px w-8 bg-gray-200" />
                  <span title={hodFullDate(l.changedAt)}>
                    {l.changer?.name || 'Someone'} marked this <LabStatusBadge status={l.status} className="mx-0.5 align-middle" /> · {hodTimeAgo(l.changedAt)}
                  </span>
                  <span className="h-px w-8 bg-gray-200" />
                </div>
              );
            }
            const c = item.data;
            const mine = c.author?.staffId && c.author.staffId === currentStaffId;
            const canDelete = isSuperAdmin || mine;
            const isLab = c.authorRole === 'lab';
            return (
              <div key={item.key} className="flex items-start gap-2.5">
                <HodAvatar person={c.author} size="h-8 w-8" textSize="text-xs" />
                <div className="min-w-0 max-w-[85%]">
                  <div className={`rounded-2xl px-3.5 py-2 ${isLab ? 'bg-teal-50 ring-1 ring-teal-100' : 'bg-gray-100'}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-gray-900">{c.author?.name || 'Unknown'}</span>
                      <RoleChip role={c.authorRole} />
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-800">{c.comment}</p>
                  </div>
                  <div className="mt-1 flex items-center gap-3 pl-3 text-[11px] text-gray-400">
                    <span title={hodFullDate(c.createdAt)}>{hodTimeAgo(c.createdAt)}</span>
                    {canDelete && (
                      <button type="button" onClick={() => onDeleteComment(job.jobId, c.commentId)} className="font-semibold hover:text-red-600">
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-end gap-2.5 border-t border-gray-100 pt-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          maxLength={2000}
          placeholder={composerPlaceholder || 'Write an update…  (Enter to post, Shift+Enter for a new line)'}
          className="min-h-[44px] flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm outline-none transition focus:border-teal-300 focus:bg-white focus:ring-2 focus:ring-teal-100"
        />
        <button
          type="button"
          onClick={submit}
          disabled={sending || !text.trim()}
          className="shrink-0 rounded-full bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? 'Posting…' : 'Post'}
        </button>
      </div>
    </div>
  );
}
